/**
 * WMS1100_R01 라벨 데이터 빌더
 * Store 데이터에서 라벨 출력용 데이터를 추출
 */

import { LabelPrintData } from "src/apis/labelApi";
import { formatDate } from "src/utils/string/stringUtil";

/** 수량 포맷 (콤마 + 단위) */
const formatQty = (qty: number | undefined, unit: string): string => {
	if (!qty && qty !== 0) return '';
	return `${qty.toLocaleString()}${unit || 'EA'}`;
};

export interface WMS1100_R01StoreData {
	WMS1100_R01LotMap: Map<string, any[]>;
	WMS1100_R01DetailList: any[];
	selectedWMS1100_R01: any;
}

/**
 * LOT 정보와 Store 데이터를 기반으로 라벨 출력 데이터 생성
 */
export const WMS1100_R01_buildLabelData = (
	lotNo: string,
	itemCd: string,
	store: WMS1100_R01StoreData
): LabelPrintData => {
	const {
		WMS1100_R01LotMap,
		WMS1100_R01DetailList,
		selectedWMS1100_R01
	} = store;

	const lotList = WMS1100_R01LotMap.get(itemCd) || [];
	const lot = lotList.find((l: any) => l.lotNo === lotNo);
	const detail = WMS1100_R01DetailList.find((d: any) => d.itemCd === itemCd);
	const header = selectedWMS1100_R01;

	if (!lot) throw new Error(`LOT를 찾을 수 없습니다: ${lotNo}`);
	if (!detail) throw new Error(`품목 정보를 찾을 수 없습니다: ${itemCd}`);
	if (!header) throw new Error('입고 정보를 찾을 수 없습니다');

	const transportDate = header.orderDt
		? formatDate(new Date(header.orderDt))
		: formatDate(new Date());

	return {
		lotNo: lot.lotNo || '',
		itemCd: detail.itemCd || '',
		itemNm: detail.itemNm || '',
		itemSpec: detail.itemSpec || '',
		transportDate,
		itemAccountNm: detail.itemAccountNm || header.inoutMethod || '',
		storageLocation: detail.locationNm || detail.locationCd || header.warehouseNm || header.warehouseCd || '',
		qty: formatQty(lot.lotQty, detail.orderUnit || 'EA'),
		customer: header.customNm || '',
		customerItemCd: detail.customerItemCd || detail.itemCd || '',
		labelType: '[자재입고]',
	};
};

/**
 * valueIndex로 LOT를 찾아서 라벨 데이터 생성
 */
export const WMS1100_R01_buildLabelDataByValueIndex = (
	valueIndex: number,
	itemCd: string,
	store: WMS1100_R01StoreData
): LabelPrintData | null => {
	const { WMS1100_R01LotMap } = store;
	const lotList = WMS1100_R01LotMap.get(itemCd) || [];
	const lot = lotList.find((l: any) => l.valueIndex === valueIndex);

	if (!lot) {
		console.warn(`LOT를 찾을 수 없습니다: valueIndex=${valueIndex}, itemCd=${itemCd}`);
		return null;
	}

	try {
		return WMS1100_R01_buildLabelData(lot.lotNo, itemCd, store);
	} catch (error) {
		console.error('라벨 데이터 생성 실패:', error);
		return null;
	}
};

/**
 * 선택된 여러 LOT(lotNo 배열)로 라벨 출력용 데이터 배열 생성
 * WMS1100_R01LotList(현재 디테일의 LOT 목록)에서 각 lotNo에 해당하는 행을 찾아 itemCd를 구하고,
 * 기존 WMS1100_R01_buildLabelData로 1건씩 생성해 배열로 반환
 */
export const WMS1100_R01_buildLabelDataListFromSelectedLots = (
	selectedLotNos: string[],
	lotList: any[],
	store: WMS1100_R01StoreData
): LabelPrintData[] => {
	if (!selectedLotNos?.length || !lotList?.length) return [];

	const lotMap = new Map<string, any[]>();
	lotList.forEach((lot: any) => {
		const itemCd = lot.itemCd ?? '';
		if (!lotMap.has(itemCd)) lotMap.set(itemCd, []);
		lotMap.get(itemCd)!.push(lot);
	});

	const fullStore: WMS1100_R01StoreData = {
		...store,
		WMS1100_R01LotMap: lotMap,
	};

	const result: LabelPrintData[] = [];
	for (const lotNo of selectedLotNos) {
		const lot = lotList.find((l: any) => l.lotNo === lotNo);
		if (!lot?.itemCd) {
			console.warn(`라벨 데이터 건너뜀: LOT를 찾을 수 없음 lotNo=${lotNo}`);
			continue;
		}
		try {
			result.push(WMS1100_R01_buildLabelData(lotNo, lot.itemCd, fullStore));
		} catch (err) {
			console.error(`라벨 데이터 생성 실패 lotNo=${lotNo}`, err);
		}
	}
	return result;
};

/**
 * 전체 LotMap에서 미출력 LOT 라벨 데이터 추출 (저장 후 자동 출력용)
 */
export const WMS1100_R01_extractUnprintedLabelData = (
	store: WMS1100_R01StoreData,
	deletedLotList: string[] = []
): LabelPrintData[] => {
	const { WMS1100_R01LotMap } = store;
	const unprintedLabelData: LabelPrintData[] = [];
	const deletedSet = new Set(deletedLotList);

	WMS1100_R01LotMap.forEach((lotList, itemCd) => {
		lotList.forEach((lot: any) => {
			const valueIndex = lot.valueIndex?.toString() || '';
			if (
				!deletedSet.has(valueIndex) &&
				!lot.printed &&
				lot.lotQty > 0 &&
				lot.inserted === true
			) {
				try {
					unprintedLabelData.push(WMS1100_R01_buildLabelData(lot.lotNo, itemCd, store));
				} catch (error) {
					console.error(`✗ 라벨 데이터 구성 실패: ${lot.lotNo}`, error);
				}
			}
		});
	});

	return unprintedLabelData;
};
